import { useEffect, useState } from "react";
import axios from "axios";
import { Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const Stars = ({ rating, size = "sm", onSelect }) => {
  const px = size === "lg" ? "w-6 h-6" : size === "md" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={onSelect ? () => onSelect(n) : undefined}
          disabled={!onSelect}
          className={onSelect ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
        >
          <Star
            className={`${px} ${n <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-gray-600"}`}
          />
        </button>
      ))}
    </div>
  );
};

const ProductReviews = ({ productId }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState({ reviews: [], average_rating: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });

  const fetchReviews = async () => {
    try {
      const r = await axios.get(`${API}/products/${productId}/reviews`);
      setData(r.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) {
      toast.error("Please write your review");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Review submitted! Thank you.");
      setForm({ rating: 5, title: "", body: "" });
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit review";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const userAlreadyReviewed = user && data.reviews.some((r) => r.user_id === user.user_id);

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mt-8" data-testid="product-reviews-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-wide" data-testid="reviews-heading">CUSTOMER REVIEWS</h2>
          {data.count > 0 ? (
            <div className="flex items-center gap-3 mt-1">
              <Stars rating={Math.round(data.average_rating)} size="md" />
              <span className="text-gray-300 text-sm">
                <span className="text-white font-bold">{data.average_rating}</span> · {data.count} review{data.count === 1 ? '' : 's'}
              </span>
            </div>
          ) : (
            <p className="text-gray-500 text-sm mt-1">Be the first to review</p>
          )}
        </div>
        {user ? (
          !userAlreadyReviewed && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wide"
              data-testid="write-review-btn"
            >
              Write a Review
            </button>
          )
        ) : (
          <Link
            to="/login"
            className="text-[#D62828] hover:text-white text-sm uppercase tracking-wide font-bold"
          >
            Log in to write a review →
          </Link>
        )}
      </div>

      {/* Review form */}
      {user && showForm && (
        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6" data-testid="review-form">
          <div className="mb-4">
            <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Your Rating</label>
            <Stars rating={form.rating} size="lg" onSelect={(n) => setForm((p) => ({ ...p, rating: n }))} />
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Title (optional)</label>
            <input
              type="text"
              maxLength={120}
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Sum it up in one line"
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none"
              data-testid="review-title-input"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Your Review *</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              rows={4}
              required
              placeholder="What did you like? How did the product perform? Would you recommend it?"
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none"
              data-testid="review-body-input"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm text-xs font-bold uppercase tracking-wide disabled:opacity-50"
              data-testid="submit-review-btn"
            >
              {submitting ? "Posting..." : "Post Review"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-white text-xs px-3 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {userAlreadyReviewed && (
        <p className="text-gray-500 text-sm italic mb-4">You've already reviewed this product.</p>
      )}

      {/* Review list */}
      {data.reviews.length === 0 ? (
        <p className="text-gray-500 text-sm">No reviews yet.</p>
      ) : (
        <div className="space-y-5" data-testid="reviews-list">
          {data.reviews.map((r) => (
            <div key={r.review_id} className="border-b border-white/5 pb-5 last:border-0" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Stars rating={r.rating} />
                  {r.title && <span className="text-white font-semibold text-sm">{r.title}</span>}
                </div>
                <span className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                <span>{r.user_name}</span>
                {r.verified_purchase && (
                  <span className="inline-flex items-center gap-1 text-green-400">
                    <BadgeCheck className="w-3 h-3" /> Verified Purchase
                  </span>
                )}
              </div>
              <p className="text-gray-300 text-sm whitespace-pre-line">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
